import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, QrCode, Store } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { PaymentPanel } from "@/components/PaymentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toMinor } from "@/lib/format";
import { advertisedBusinessPath } from "@/lib/whiteLabel";

const POLL_MS = 4000;
const OPEN_STATUSES = ["pending", "awaiting_confirmation"];

/** Standalone "pay this shop" counter payment — the amount-entry route to a counter payment named
 * in docs/archive/2026-08-decisions-payment-links.md; a camera QR scan (mobile's other route) isn't a natural
 * desktop-browser interaction, so it isn't built here. */
export default function Pay() {
  const { businessId } = useParams();
  const { t } = useApp();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  // Two distinct failures, deliberately NOT one flag. A 404 means this shop genuinely is not on
  // this city page and the only useful action is to go back; anything else (offline, 5xx, a
  // timeout at the counter) means the shop is fine and the payer should be able to retry without
  // losing the page. Collapsing both into "missing" told someone standing at the till that the
  // shop does not exist because their signal dropped for a second.
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [payment, setPayment] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadBusiness = useCallback(() => {
    setNotFound(false);
    setLoadError(false);
    api
      .get(`/directory/businesses/${businessId}`)
      .then((r) => setBusiness(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      });
  }, [businessId]);

  useEffect(() => {
    loadBusiness();
  }, [loadBusiness]);

  useEffect(() => {
    if (!payment || !OPEN_STATUSES.includes(payment.status)) return;
    const interval = setInterval(() => {
      api.get(`/payments/${payment.id}`).then((r) => setPayment(r.data));
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [payment]);

  const createQr = async () => {
    setError("");
    setCreating(true);
    try {
      const { data } = await api.post("/payments/counter", {
        business_id: businessId,
        // CounterPaymentIn is Strict and takes `amount_minor_units` (paise); the payer types
        // rupees, so ×100. Sending `amount` (rupees) was an extra field → 422 (same class as F-05).
        amount_minor_units: toMinor(amount),
        note: note.trim(),
      });
      setPayment(data);
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setCreating(false);
    }
  };

  const claim = async (utr) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${payment.id}/claim`, { utr });
      setPayment(data);
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/cancel`);
      setPayment(null);
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="pay-not-found">
        <p className="text-lg">{t("payment.shopNotFound")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="pay-back-btn">
          {t("landmark.backToCity")}
        </Button>
      </div>
    );
  if (loadError)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="pay-load-error">
        <p className="text-lg">{t("payment.loadFailed")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button className="rounded-full" onClick={loadBusiness} data-testid="pay-retry-btn">
            {t("common.retry")}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate("/")}
            data-testid="pay-back-btn"
          >
            {t("landmark.backToCity")}
          </Button>
        </div>
      </div>
    );
  if (!business)
    return (
      <div className="mx-auto max-w-lg space-y-4 px-5 py-12 sm:px-8" data-testid="pay-loading">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );

  return (
    <div className="mx-auto max-w-lg px-5 py-12 sm:px-8" data-testid="pay-shop-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-5 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="pay-back-link"
      >
        <ArrowLeft size={14} /> {t("landmark.backToCity")}
      </button>

      <h1 className="font-heading text-3xl font-bold tracking-tight">{t("payment.payThisShop")}</h1>
      <p className="mt-2 text-muted-foreground">{business.name}</p>

      {!business.accepts_upi ? (
        /* The visitor came here specifically to hand over money. "Not set up" on its own ends the
         * errand; the two actions below turn it back into contact the shop can still act on. */
        <div
          className="mt-8 rounded-2xl border border-dashed border-border p-8 text-center"
          data-testid="pay-not-set-up"
        >
          <p className="text-muted-foreground">{t("payment.notSetUp")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("payment.notSetUpHint")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {business.phone && (
              <Button asChild className="rounded-full">
                <a href={`tel:${business.phone}`} data-testid="pay-call-shop-btn">
                  <Phone size={15} className="mr-2" />
                  {t("payment.callShop")}
                </a>
              </Button>
            )}
            {business.slug && (
              <Button asChild variant="outline" className="rounded-full">
                <Link to={advertisedBusinessPath(business)} data-testid="pay-shop-page-btn">
                  <Store size={15} className="mr-2" />
                  {t("payment.seeShopPage")}
                </Link>
              </Button>
            )}
          </div>
        </div>
      ) : !payment ? (
        <div className="mt-8 space-y-4 rounded-2xl border border-border p-6" data-testid="pay-shop-form">
          <div className="space-y-1.5">
            <Label htmlFor="pay-amount">{t("payment.amountLabel")}</Label>
            <Input
              id="pay-amount"
              type="number"
              min={1}
              data-testid="pay-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-note">{t("payment.noteLabel")}</Label>
            <Input id="pay-note" data-testid="pay-note-input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <Button
            className="rounded-full"
            disabled={!(Number(amount) > 0) || creating}
            onClick={createQr}
            data-testid="pay-create-btn"
          >
            <QrCode size={15} className="mr-2" />
            {creating ? t("common.saving") : t("payment.createQr")}
          </Button>
        </div>
      ) : (
        <PaymentPanel
          payment={payment}
          requireUtr={false}
          onClaim={claim}
          onCancel={cancel}
          busy={busy}
          error={error}
          testId="pay-shop-panel"
        />
      )}
    </div>
  );
}
